import {
  BULK_TRANSFER_DEFAULT_MAX_BYTES,
  type BulkTransferChunk,
  type BulkTransferStreamEvent,
  type EnvironmentApi,
  type EnvironmentTransferOptions,
  type GitRunStackedActionInput,
  type GitRunStackedActionResult,
  type GitStatusResult,
  type GitStatusStreamEvent,
  type LocalApi,
  ORCHESTRATION_WS_METHODS,
  type ServerSettingsPatch,
  WS_METHODS,
} from "@cmmd-forge/contracts";
import {
  assembleBulkTransfer,
  chunkBulkTransfer,
  type ChunkedBulkTransfer,
} from "@cmmd-forge/shared/bulkTransfer";
import { applyGitStatusStreamEvent } from "@cmmd-forge/shared/git";
import { Stream } from "effect";

import { type WsRpcProtocolClient } from "./protocol";
import type {
  RpcInput,
  RpcInputStreamMethod,
  RpcStreamMethod,
  RpcUnaryMethod,
  RpcUnaryNoArgMethod,
  StreamSubscriptionOptions,
} from "./rpcClientTypes";
import { createTerminalControlClient, type TerminalRpcClient } from "./terminalControlClient";
import type { WsRpcOrchestrationClient } from "./wsRpcOrchestrationClient";
import type { GitRunStackedActionOptions } from "./wsRpcGitTypes";
import { isTransportConnectionErrorMessage } from "./transportError";
import { resetWsReconnectBackoff } from "./wsConnectionState";
import { WsRequestTimeoutError, WsTransport } from "./wsTransport";
import { createWsRpcAttachments, type WsRpcAttachments } from "./wsRpcAttachments";
import { createWsRpcWorkspace, type WsRpcWorkspace } from "./wsRpcWorkspace";
import { cancelWorkspaceFileWrite } from "./workspaceFileWriteCancellation";

const DISPATCH_COMMAND_ACK_TIMEOUT_MS = 20_000;
const USAGE_SCAN_TIMEOUT_MS = 120_000;
const BOOTSTRAP_COMMAND_ACK_TIMEOUT_MS = 300_000;

function isRetryableDispatchTransportError(error: unknown): boolean {
  if (error instanceof WsRequestTimeoutError) return true;
  const message = error instanceof Error ? error.message : String(error);
  return isTransportConnectionErrorMessage(message);
}

async function dispatchCommandWithBoundedReplay(
  transport: WsTransport,
  input: RpcInput<typeof ORCHESTRATION_WS_METHODS.dispatchCommand>,
): ReturnType<RpcUnaryMethod<typeof ORCHESTRATION_WS_METHODS.dispatchCommand>> {
  const timeout =
    input.type === "thread.turn.start" && input.bootstrap !== undefined
      ? BOOTSTRAP_COMMAND_ACK_TIMEOUT_MS
      : DISPATCH_COMMAND_ACK_TIMEOUT_MS;
  const execute = () =>
    transport.request((client) => client[ORCHESTRATION_WS_METHODS.dispatchCommand](input), {
      timeout,
    });
  try {
    return await execute();
  } catch (error) {
    if (!isRetryableDispatchTransportError(error)) throw error;
    await transport.reconnect();
    return await execute();
  }
}

type BulkTransferStartedEvent = Extract<BulkTransferStreamEvent, { readonly kind: "started" }>;
type BulkTransferCompletedEvent = Extract<BulkTransferStreamEvent, { readonly kind: "completed" }>;

interface BulkTransferReceiveState {
  started: BulkTransferStartedEvent | null;
  completed: BulkTransferCompletedEvent | null;
  readonly chunks: Array<BulkTransferChunk>;
  protocolError: Error | null;
  receivedBytes: number;
}

function throwIfTransferAborted(signal: AbortSignal | undefined): void {
  if (!signal?.aborted) return;
  const error = new Error("Bulk transfer was cancelled.");
  error.name = "AbortError";
  throw error;
}

async function receiveBulkTransfer(
  transport: WsTransport,
  connect: (client: WsRpcProtocolClient) => Stream.Stream<BulkTransferStreamEvent, Error, never>,
  options?: EnvironmentTransferOptions,
): Promise<Uint8Array> {
  const state: BulkTransferReceiveState = {
    started: null,
    completed: null,
    chunks: [],
    protocolError: null,
    receivedBytes: 0,
  };

  await transport.requestStream(
    connect,
    (event) => handleBulkTransferEvent(state, event, options),
    options,
  );

  if (state.protocolError !== null) throw state.protocolError;
  const startedEvent = state.started;
  const completedEvent = state.completed;
  if (startedEvent === null || completedEvent === null) {
    throw new Error("Bulk transfer stream completed without start and completion metadata.");
  }
  if (
    completedEvent.transferId !== startedEvent.transferId ||
    completedEvent.totalBytes !== startedEvent.totalBytes ||
    completedEvent.sha256 !== startedEvent.sha256
  ) {
    throw new Error("Bulk transfer completion metadata mismatch.");
  }
  return await assembleBulkTransfer({
    totalBytes: startedEvent.totalBytes,
    sha256: startedEvent.sha256,
    chunks: state.chunks,
  } satisfies ChunkedBulkTransfer);
}

function handleBulkTransferEvent(
  state: BulkTransferReceiveState,
  event: BulkTransferStreamEvent,
  options: EnvironmentTransferOptions | undefined,
): void {
  if (state.protocolError !== null) return;
  if (event.kind === "started") {
    handleBulkTransferStarted(state, event, options);
    return;
  }
  if (state.started === null || event.transferId !== state.started.transferId) {
    state.protocolError = new Error("Bulk transfer event identity mismatch.");
    return;
  }
  if (event.kind === "chunk") {
    handleBulkTransferChunk(state, event, options);
    return;
  }
  state.completed = event;
  options?.onProgress?.({ completedBytes: event.totalBytes, totalBytes: event.totalBytes });
}

function handleBulkTransferStarted(
  state: BulkTransferReceiveState,
  event: BulkTransferStartedEvent,
  options: EnvironmentTransferOptions | undefined,
): void {
  if (state.started !== null || event.totalBytes > BULK_TRANSFER_DEFAULT_MAX_BYTES) {
    state.protocolError = new Error("Bulk transfer started with invalid or oversized metadata.");
    return;
  }
  state.started = event;
  options?.onProgress?.({ completedBytes: 0, totalBytes: event.totalBytes });
}

function handleBulkTransferChunk(
  state: BulkTransferReceiveState,
  event: BulkTransferChunk,
  options: EnvironmentTransferOptions | undefined,
): void {
  const started = state.started;
  if (started === null) return;
  state.receivedBytes += event.bytes;
  if (state.receivedBytes > started.totalBytes) {
    state.protocolError = new Error("Bulk transfer exceeded its declared byte count.");
    return;
  }
  state.chunks.push(event);
  options?.onProgress?.({
    completedBytes: state.receivedBytes,
    totalBytes: started.totalBytes,
  });
}

async function writeWorkspaceFile(
  transport: WsTransport,
  input: RpcInput<typeof WS_METHODS.projectsWriteFile>,
  options?: EnvironmentTransferOptions,
): ReturnType<RpcUnaryMethod<typeof WS_METHODS.projectsWriteFile>> {
  const bytes = new TextEncoder().encode(input.contents);
  if (bytes.byteLength > BULK_TRANSFER_DEFAULT_MAX_BYTES) {
    throw new Error(
      `Project file exceeds the ${BULK_TRANSFER_DEFAULT_MAX_BYTES}-byte write limit.`,
    );
  }
  const transfer = await chunkBulkTransfer(bytes);
  const transferId = transfer.chunks[0]?.transferId ?? crypto.randomUUID().replaceAll("-", "_");
  let begun = false;
  try {
    throwIfTransferAborted(options?.signal);
    const begin = await beginWorkspaceFileWrite(transport, input, transferId, transfer);
    begun = true;
    options?.onProgress?.({
      completedBytes: begin.nextOffset,
      totalBytes: transfer.totalBytes,
    });
    await uploadWorkspaceFileChunks(transport, transferId, transfer, begin, options);
    return { relativePath: input.relativePath };
  } catch (error) {
    if (begun) await cancelWorkspaceFileWrite(transport, transferId);
    throw error;
  }
}

function beginWorkspaceFileWrite(
  transport: WsTransport,
  input: RpcInput<typeof WS_METHODS.projectsWriteFile>,
  transferId: string,
  transfer: ChunkedBulkTransfer,
) {
  return transport.request((client) =>
    client[WS_METHODS.workspaceBeginWriteFile]({
      transferId,
      cwd: input.cwd,
      relativePath: input.relativePath,
      totalBytes: transfer.totalBytes,
      sha256: transfer.sha256,
    }),
  );
}

async function uploadWorkspaceFileChunks(
  transport: WsTransport,
  transferId: string,
  transfer: ChunkedBulkTransfer,
  begin: { readonly nextOffset: number; readonly state: "open" | "completed" },
  options?: EnvironmentTransferOptions,
): Promise<void> {
  if (begin.state === "completed") return;
  for (const chunk of transfer.chunks) {
    if (chunk.offset < begin.nextOffset) continue;
    throwIfTransferAborted(options?.signal);
    const { kind: _kind, ...writeChunk } = chunk;
    const accepted = await transport.request((client) =>
      client[WS_METHODS.workspaceWriteFileChunk](writeChunk),
    );
    options?.onProgress?.({
      completedBytes: accepted.nextOffset,
      totalBytes: transfer.totalBytes,
    });
  }
  throwIfTransferAborted(options?.signal);
  await transport.request((client) => client[WS_METHODS.workspaceCommitWriteFile]({ transferId }));
}

export interface WsRpcClient {
  readonly dispose: () => Promise<void>;
  readonly reconnect: () => Promise<void>;
  readonly terminal: TerminalRpcClient;
  readonly browser?: {
    readonly openSession: RpcUnaryMethod<typeof WS_METHODS.browserOpenSession>;
    readonly listSessions: RpcUnaryMethod<typeof WS_METHODS.browserListSessions>;
    readonly closeSession: RpcUnaryMethod<typeof WS_METHODS.browserCloseSession>;
    readonly getSession: RpcUnaryMethod<typeof WS_METHODS.browserGetSession>;
    readonly command: RpcUnaryMethod<typeof WS_METHODS.browserCommand>;
    readonly clearBrowsingData: RpcUnaryMethod<typeof WS_METHODS.browserClearBrowsingData>;
    readonly attachDisplay: RpcUnaryMethod<typeof WS_METHODS.browserAttachDisplay>;
    readonly addDisplayIceCandidate: RpcUnaryMethod<
      typeof WS_METHODS.browserAddDisplayIceCandidate
    >;
    readonly onEvent: RpcInputStreamMethod<typeof WS_METHODS.subscribeBrowserEvents>;
  };
  readonly computer: {
    readonly input: RpcUnaryMethod<typeof WS_METHODS.computerInput>;
  };
  readonly threadDevRuntime: {
    readonly get: RpcUnaryMethod<typeof WS_METHODS.threadDevRuntimeGet>;
    readonly allocatePort: RpcUnaryMethod<typeof WS_METHODS.threadDevRuntimeAllocatePort>;
    readonly release: RpcUnaryMethod<typeof WS_METHODS.threadDevRuntimeRelease>;
  };
  readonly devTunnel: {
    readonly register: RpcUnaryMethod<typeof WS_METHODS.devTunnelRegister>;
    readonly unregister: RpcUnaryMethod<typeof WS_METHODS.devTunnelUnregister>;
    readonly list: RpcUnaryMethod<typeof WS_METHODS.devTunnelList>;
  };
  readonly attachments: WsRpcAttachments;
  readonly projects: {
    readonly searchContents: EnvironmentApi["projects"]["searchContents"];
    readonly searchEntries: RpcUnaryMethod<typeof WS_METHODS.projectsSearchEntries>;
    readonly readFile: (
      input: RpcInput<typeof WS_METHODS.workspaceReadFile>,
      options?: EnvironmentTransferOptions,
    ) => Promise<Uint8Array>;
    readonly writeFile: (
      input: RpcInput<typeof WS_METHODS.projectsWriteFile>,
      options?: EnvironmentTransferOptions,
    ) => ReturnType<RpcUnaryMethod<typeof WS_METHODS.projectsWriteFile>>;
  };
  readonly projectCheckoutDeletion: {
    readonly preview: RpcUnaryMethod<typeof WS_METHODS.projectCheckoutDeletionPreview>;
    readonly execute: RpcUnaryMethod<typeof WS_METHODS.projectCheckoutDeletionExecute>;
  };
  readonly workspace: WsRpcWorkspace;
  readonly shell: {
    readonly openInEditor: (input: {
      readonly cwd: Parameters<LocalApi["shell"]["openInEditor"]>[0];
      readonly editor: Parameters<LocalApi["shell"]["openInEditor"]>[1];
    }) => ReturnType<LocalApi["shell"]["openInEditor"]>;
  };
  readonly git: {
    readonly pull: RpcUnaryMethod<typeof WS_METHODS.gitPull>;
    readonly refreshStatus: RpcUnaryMethod<typeof WS_METHODS.gitRefreshStatus>;
    readonly onStatus: (
      input: RpcInput<typeof WS_METHODS.subscribeGitStatus>,
      listener: (status: GitStatusResult) => void,
      options?: StreamSubscriptionOptions,
    ) => () => void;
    readonly runStackedAction: (
      input: GitRunStackedActionInput,
      options?: GitRunStackedActionOptions,
    ) => Promise<GitRunStackedActionResult>;
    readonly listBranches: RpcUnaryMethod<typeof WS_METHODS.gitListBranches>;
    readonly createWorktree: RpcUnaryMethod<typeof WS_METHODS.gitCreateWorktree>;
    readonly removeWorktree: RpcUnaryMethod<typeof WS_METHODS.gitRemoveWorktree>;
    readonly createBranch: RpcUnaryMethod<typeof WS_METHODS.gitCreateBranch>;
    readonly checkout: RpcUnaryMethod<typeof WS_METHODS.gitCheckout>;
    readonly init: RpcUnaryMethod<typeof WS_METHODS.gitInit>;
    readonly resolvePullRequest: RpcUnaryMethod<typeof WS_METHODS.gitResolvePullRequest>;
    readonly preparePullRequestThread: RpcUnaryMethod<
      typeof WS_METHODS.gitPreparePullRequestThread
    >;
  };
  readonly pullRequests: {
    readonly list: RpcUnaryMethod<typeof WS_METHODS.pullRequestsList>;
    readonly listStats: RpcUnaryMethod<typeof WS_METHODS.pullRequestsListStats>;
    readonly detail: RpcUnaryMethod<typeof WS_METHODS.pullRequestsDetail>;
    readonly activity: RpcUnaryMethod<typeof WS_METHODS.pullRequestsActivity>;
    readonly threadComments: RpcUnaryMethod<typeof WS_METHODS.pullRequestsThreadComments>;
    readonly diffFileContents: RpcUnaryMethod<typeof WS_METHODS.pullRequestsDiffFileContents>;
    readonly runAction: RpcUnaryMethod<typeof WS_METHODS.pullRequestsRunAction>;
    readonly update: RpcUnaryMethod<typeof WS_METHODS.pullRequestsUpdate>;
    readonly comment: RpcUnaryMethod<typeof WS_METHODS.pullRequestsComment>;
    readonly updateComment: RpcUnaryMethod<typeof WS_METHODS.pullRequestsUpdateComment>;
    readonly submitReview: RpcUnaryMethod<typeof WS_METHODS.pullRequestsSubmitReview>;
    readonly replyToThread: RpcUnaryMethod<typeof WS_METHODS.pullRequestsReplyToThread>;
    readonly setThreadResolution: RpcUnaryMethod<typeof WS_METHODS.pullRequestsSetThreadResolution>;
    readonly setReaction: RpcUnaryMethod<typeof WS_METHODS.pullRequestsSetReaction>;
    readonly invalidate: RpcUnaryMethod<typeof WS_METHODS.pullRequestsInvalidate>;
    readonly reviewerCandidates: RpcUnaryMethod<typeof WS_METHODS.pullRequestsReviewerCandidates>;
    readonly requestReviewers: RpcUnaryMethod<typeof WS_METHODS.pullRequestsRequestReviewers>;
  };
  readonly server: {
    readonly getConfig: RpcUnaryNoArgMethod<typeof WS_METHODS.serverGetConfig>;
    readonly refreshProviders: RpcUnaryNoArgMethod<typeof WS_METHODS.serverRefreshProviders>;
    readonly startProviderAuth: RpcUnaryMethod<typeof WS_METHODS.serverStartProviderAuth>;
    readonly repairProviderAuthRuntime: RpcUnaryMethod<
      typeof WS_METHODS.serverRepairProviderAuthRuntime
    >;
    readonly disconnectProviderAuth: RpcUnaryMethod<typeof WS_METHODS.serverDisconnectProviderAuth>;
    readonly getProviderAuthSession: RpcUnaryMethod<typeof WS_METHODS.serverGetProviderAuthSession>;
    readonly cancelProviderAuth: RpcUnaryMethod<typeof WS_METHODS.serverCancelProviderAuth>;
    readonly submitProviderAuthCode: RpcUnaryMethod<typeof WS_METHODS.serverSubmitProviderAuthCode>;
    readonly syncPersonalCodexToCloud: RpcUnaryMethod<
      typeof WS_METHODS.serverSyncPersonalCodexToCloud
    >;
    readonly syncPersonalClaudeToCloud: RpcUnaryMethod<
      typeof WS_METHODS.serverSyncPersonalClaudeToCloud
    >;
    readonly upsertKeybinding: RpcUnaryMethod<typeof WS_METHODS.serverUpsertKeybinding>;
    readonly removeKeybinding: RpcUnaryMethod<typeof WS_METHODS.serverRemoveKeybinding>;
    readonly getSettings: RpcUnaryNoArgMethod<typeof WS_METHODS.serverGetSettings>;
    readonly updateSettings: (
      patch: ServerSettingsPatch,
    ) => ReturnType<RpcUnaryMethod<typeof WS_METHODS.serverUpdateSettings>>;
    readonly updateEnvironmentDisplayName: RpcUnaryMethod<
      typeof WS_METHODS.serverUpdateEnvironmentDisplayName
    >;
    /** Summarizes this server's own rotated trace file. */
    readonly getTraceDiagnostics: RpcUnaryMethod<typeof WS_METHODS.serverGetTraceDiagnostics>;
    readonly subscribeConfig: RpcStreamMethod<typeof WS_METHODS.subscribeServerConfig>;
    readonly subscribeLifecycle: RpcStreamMethod<typeof WS_METHODS.subscribeServerLifecycle>;
    readonly subscribeAuthAccess: RpcStreamMethod<typeof WS_METHODS.subscribeAuthAccess>;
    readonly subscribeWorkspaceReadiness: RpcStreamMethod<
      typeof WS_METHODS.subscribeWorkspaceReadiness
    >;
  };
  readonly usage: {
    /** Scans this environment's Claude Code and Codex transcripts for a window. */
    readonly getSummary: RpcUnaryMethod<typeof WS_METHODS.usageGetSummary>;
  };
  readonly orchestration: WsRpcOrchestrationClient;
}

export function createWsRpcClient(transport: WsTransport): WsRpcClient {
  const terminalControlClient = createTerminalControlClient(transport);
  return {
    dispose: () => {
      terminalControlClient.dispose();
      return transport.dispose();
    },
    reconnect: async () => {
      resetWsReconnectBackoff();
      await transport.reconnect();
    },
    terminal: terminalControlClient.terminal,
    browser: {
      openSession: (input) =>
        transport.request((client) => client[WS_METHODS.browserOpenSession](input)),
      listSessions: (input) =>
        transport.request((client) => client[WS_METHODS.browserListSessions](input)),
      closeSession: (input) =>
        transport.request((client) => client[WS_METHODS.browserCloseSession](input)),
      getSession: (input) =>
        transport.request((client) => client[WS_METHODS.browserGetSession](input)),
      command: (input) => transport.request((client) => client[WS_METHODS.browserCommand](input)),
      clearBrowsingData: (input) =>
        transport.request((client) => client[WS_METHODS.browserClearBrowsingData](input)),
      attachDisplay: (input) =>
        transport.request((client) => client[WS_METHODS.browserAttachDisplay](input)),
      addDisplayIceCandidate: (input) =>
        transport.request((client) => client[WS_METHODS.browserAddDisplayIceCandidate](input)),
      onEvent: (input, listener, options) =>
        transport.subscribe(
          (client) => client[WS_METHODS.subscribeBrowserEvents](input),
          listener,
          options,
        ),
    },
    computer: {
      input: (input) => transport.request((client) => client[WS_METHODS.computerInput](input)),
    },
    threadDevRuntime: {
      get: (input) => transport.request((client) => client[WS_METHODS.threadDevRuntimeGet](input)),
      allocatePort: (input) =>
        transport.request((client) => client[WS_METHODS.threadDevRuntimeAllocatePort](input)),
      release: (input) =>
        transport.request((client) => client[WS_METHODS.threadDevRuntimeRelease](input)),
    },
    devTunnel: {
      register: (input) =>
        transport.request((client) => client[WS_METHODS.devTunnelRegister](input)),
      unregister: (input) =>
        transport.request((client) => client[WS_METHODS.devTunnelUnregister](input)),
      list: (input) => transport.request((client) => client[WS_METHODS.devTunnelList](input)),
    },
    attachments: createWsRpcAttachments(transport),
    projects: {
      searchContents: (input, options) =>
        transport.request((client) => client[WS_METHODS.projectsSearchContents](input), options),
      searchEntries: (input) =>
        transport.request((client) => client[WS_METHODS.projectsSearchEntries](input)),
      readFile: (input, options) =>
        receiveBulkTransfer(
          transport,
          (client) => client[WS_METHODS.workspaceReadFile](input),
          options,
        ),
      writeFile: (input, options) => writeWorkspaceFile(transport, input, options),
    },
    projectCheckoutDeletion: {
      preview: (input) =>
        transport.request((client) => client[WS_METHODS.projectCheckoutDeletionPreview](input)),
      execute: (input) =>
        transport.request((client) => client[WS_METHODS.projectCheckoutDeletionExecute](input)),
    },
    workspace: createWsRpcWorkspace(transport),
    shell: {
      openInEditor: (input) =>
        transport.request((client) => client[WS_METHODS.shellOpenInEditor](input)),
    },
    git: {
      pull: (input) => transport.request((client) => client[WS_METHODS.gitPull](input)),
      refreshStatus: (input) =>
        transport.request((client) => client[WS_METHODS.gitRefreshStatus](input)),
      onStatus: (input, listener, options) => {
        let current: GitStatusResult | null = null;
        return transport.subscribe(
          (client) => client[WS_METHODS.subscribeGitStatus](input),
          (event: GitStatusStreamEvent) => {
            current = applyGitStatusStreamEvent(current, event);
            listener(current);
          },
          options,
        );
      },
      runStackedAction: async (input, options) => {
        let result: GitRunStackedActionResult | null = null;

        await transport.requestStream(
          (client) => client[WS_METHODS.gitRunStackedAction](input),
          (event) => {
            options?.onProgress?.(event);
            if (event.kind === "action_finished") {
              result = event.result;
            }
          },
        );

        if (result) {
          return result;
        }

        throw new Error("Git action stream completed without a final result.");
      },
      listBranches: (input) =>
        transport.request((client) => client[WS_METHODS.gitListBranches](input)),
      createWorktree: (input) =>
        transport.request((client) => client[WS_METHODS.gitCreateWorktree](input)),
      removeWorktree: (input) =>
        transport.request((client) => client[WS_METHODS.gitRemoveWorktree](input)),
      createBranch: (input) =>
        transport.request((client) => client[WS_METHODS.gitCreateBranch](input)),
      checkout: (input) => transport.request((client) => client[WS_METHODS.gitCheckout](input)),
      init: (input) => transport.request((client) => client[WS_METHODS.gitInit](input)),
      resolvePullRequest: (input) =>
        transport.request((client) => client[WS_METHODS.gitResolvePullRequest](input)),
      preparePullRequestThread: (input) =>
        transport.request((client) => client[WS_METHODS.gitPreparePullRequestThread](input)),
    },
    pullRequests: {
      list: (input) => transport.request((client) => client[WS_METHODS.pullRequestsList](input)),
      listStats: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsListStats](input)),
      detail: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsDetail](input)),
      activity: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsActivity](input)),
      threadComments: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsThreadComments](input)),
      diffFileContents: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsDiffFileContents](input)),
      runAction: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsRunAction](input)),
      update: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsUpdate](input)),
      comment: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsComment](input)),
      updateComment: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsUpdateComment](input)),
      submitReview: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsSubmitReview](input)),
      replyToThread: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsReplyToThread](input)),
      setThreadResolution: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsSetThreadResolution](input)),
      setReaction: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsSetReaction](input)),
      invalidate: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsInvalidate](input)),
      reviewerCandidates: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsReviewerCandidates](input)),
      requestReviewers: (input) =>
        transport.request((client) => client[WS_METHODS.pullRequestsRequestReviewers](input)),
    },
    server: {
      getConfig: () => transport.request((client) => client[WS_METHODS.serverGetConfig]({})),
      refreshProviders: () =>
        transport.request((client) => client[WS_METHODS.serverRefreshProviders]({})),
      startProviderAuth: (input) =>
        transport.request((client) => client[WS_METHODS.serverStartProviderAuth](input)),
      repairProviderAuthRuntime: (input) =>
        transport.request((client) => client[WS_METHODS.serverRepairProviderAuthRuntime](input)),
      disconnectProviderAuth: (input) =>
        transport.request((client) => client[WS_METHODS.serverDisconnectProviderAuth](input)),
      getProviderAuthSession: (input) =>
        transport.request((client) => client[WS_METHODS.serverGetProviderAuthSession](input)),
      cancelProviderAuth: (input) =>
        transport.request((client) => client[WS_METHODS.serverCancelProviderAuth](input)),
      submitProviderAuthCode: (input) =>
        transport.request((client) => client[WS_METHODS.serverSubmitProviderAuthCode](input)),
      syncPersonalCodexToCloud: (input) =>
        transport.request((client) => client[WS_METHODS.serverSyncPersonalCodexToCloud](input)),
      syncPersonalClaudeToCloud: (input) =>
        transport.request((client) => client[WS_METHODS.serverSyncPersonalClaudeToCloud](input)),
      upsertKeybinding: (input) =>
        transport.request((client) => client[WS_METHODS.serverUpsertKeybinding](input)),
      removeKeybinding: (input) =>
        transport.request((client) => client[WS_METHODS.serverRemoveKeybinding](input)),
      getSettings: () => transport.request((client) => client[WS_METHODS.serverGetSettings]({})),
      updateSettings: (patch) =>
        transport.request((client) => client[WS_METHODS.serverUpdateSettings]({ patch })),
      updateEnvironmentDisplayName: (input) =>
        transport.request((client) => client[WS_METHODS.serverUpdateEnvironmentDisplayName](input)),
      getTraceDiagnostics: (input) =>
        transport.request((client) => client[WS_METHODS.serverGetTraceDiagnostics](input)),
      subscribeConfig: (listener, options) =>
        transport.subscribe(
          (client) => client[WS_METHODS.subscribeServerConfig]({}),
          listener,
          options,
        ),
      subscribeLifecycle: (listener, options) =>
        transport.subscribe(
          (client) => client[WS_METHODS.subscribeServerLifecycle]({}),
          listener,
          options,
        ),
      subscribeAuthAccess: (listener, options) =>
        transport.subscribe(
          (client) => client[WS_METHODS.subscribeAuthAccess]({}),
          listener,
          options,
        ),
      subscribeWorkspaceReadiness: (listener, options) =>
        transport.subscribe(
          (client) => client[WS_METHODS.subscribeWorkspaceReadiness]({}),
          listener,
          options,
        ),
    },
    usage: {
      // A cold 30-day scan reads every transcript on the machine, which can run
      // well past the default request timeout on a busy install.
      getSummary: (input) =>
        transport.request((client) => client[WS_METHODS.usageGetSummary](input), {
          timeout: USAGE_SCAN_TIMEOUT_MS,
        }),
    },
    orchestration: {
      dispatchCommand: (input) => dispatchCommandWithBoundedReplay(transport, input),
      searchThreads: (input) =>
        transport.request((client) => client[ORCHESTRATION_WS_METHODS.searchThreads](input), {
          timeout: 10_000,
        }),
      getTurnDiff: (input, options) =>
        receiveBulkTransfer(
          transport,
          (client) => client[ORCHESTRATION_WS_METHODS.streamTurnDiff](input),
          options,
        ).then((bytes) => ({ ...input, diff: new TextDecoder().decode(bytes) })),
      getFullThreadDiff: (input, options) =>
        receiveBulkTransfer(
          transport,
          (client) => client[ORCHESTRATION_WS_METHODS.streamFullThreadDiff](input),
          options,
        ).then((bytes) => ({
          ...input,
          fromTurnCount: 0,
          diff: new TextDecoder().decode(bytes),
        })),
      subscribeShell: (listener, options) =>
        transport.subscribe(
          (client) => client[ORCHESTRATION_WS_METHODS.subscribeShell]({}),
          listener,
          options,
        ),
      subscribeThread: (input, listener, options) =>
        transport.subscribe(
          (client) => client[ORCHESTRATION_WS_METHODS.subscribeThread](input),
          listener,
          options,
        ),
    },
  };
}
